import * as yup from 'yup';

/**
 * The password rule the API enforces, transcribed once so the register, reset
 * and change-password forms cannot drift apart from it or from the server.
 *
 * The API is the source of truth. Every value below mirrors its schema, and a
 * client rule that is looser anywhere lets the user submit a password the form
 * already approved and the server then rejects.
 */

/**
 * Counted in CODE POINTS, not `String.prototype.length`.
 *
 * JS counts UTF-16 code units and the server counts code points, so a password
 * of "Aa1!" plus two astral emoji measures 8 here and 6 there. Using `.length`
 * would accept it and the server would reject it as too short.
 */
export const PASSWORD_MIN_CHARS = 8;

/**
 * Counted in UTF-8 BYTES, not characters, because the hash step caps its input
 * at this many bytes. A multibyte password crosses the cap well under any
 * plausible character limit, so a character-based maximum is the wrong unit.
 */
export const PASSWORD_MAX_BYTES = 72;

/**
 * The four ASCII punctuation ranges, and nothing else.
 *
 * This is an exact transcription of the server's set, verified character for
 * character across U+0000 to U+1FFFF with no difference in either direction.
 * A broader class such as `[^A-Za-z0-9]` counts letters like "n" with a tilde,
 * a space or a currency sign as special, which the server does not, and that
 * gap is what let the forms approve passwords the API refused.
 */
export const PASSWORD_SPECIAL_PATTERN = /[!-/:-@[-`{-~]/;

/**
 * Deliberately ASCII, and deliberately narrower than the server.
 *
 * The server tests these with Python's `isupper()` and `isdigit()`, which are
 * Unicode aware, so these classes are strict subsets of it: there is no input
 * they accept that the server refuses. Widening them to `\p{Lu}` or `\p{Nd}`
 * looks more correct and is not, because those classes each accept characters
 * the server rejects, which reopens the very gap this module closes.
 */
const PASSWORD_UPPERCASE_PATTERN = /[A-Z]/;
const PASSWORD_DIGIT_PATTERN = /[0-9]/;

export function countPasswordCodePoints(value: string): number {
  return Array.from(value).length;
}

export function countPasswordUtf8Bytes(value: string): number {
  return new TextEncoder().encode(value).length;
}

/**
 * Yup rule for a new password. The messages are translation key suffixes, not
 * copy, matching how the auth forms already map field errors.
 *
 * Length and byte cap are separate checks on purpose: they fail for different
 * reasons and a user who pasted a long multibyte passphrase needs to be told
 * which limit they hit.
 */
export const passwordRule = yup
  .string()
  .required('required')
  .test(
    'password-min-code-points',
    'too_short',
    (value) => value === undefined || countPasswordCodePoints(value) >= PASSWORD_MIN_CHARS,
  )
  .test(
    'password-max-utf8-bytes',
    'too_long',
    (value) => value === undefined || countPasswordUtf8Bytes(value) <= PASSWORD_MAX_BYTES,
  )
  .matches(PASSWORD_UPPERCASE_PATTERN, 'missing_uppercase')
  .matches(PASSWORD_DIGIT_PATTERN, 'missing_digit')
  .matches(PASSWORD_SPECIAL_PATTERN, 'missing_special');
